import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { QrCode, Copy, RefreshCw, Download, Share2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

/**
 * Share dialog — fetches a unique join URL + QR PNG (base64) and lets the creator
 * copy / regenerate / download. Visible only to event owners and admins.
 * Sharer is appended via ?ref=<my user id> so we can attribute referrals.
 */
export default function EventShareDialog({ eventKind, eventId, eventTitle, trigger }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/events/${eventKind}/${eventId}/share`);
      // Inject `?ref=<user.id>` so when someone joins via this link, we credit the sharer.
      if (user?.id && data.join_url && !data.join_url.includes("ref=")) {
        const sep = data.join_url.includes("?") ? "&" : "?";
        data.join_url = `${data.join_url}${sep}ref=${user.id}`;
      }
      setInfo(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load share info");
    } finally {
      setLoading(false);
    }
  }, [eventKind, eventId, user]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const copy = async () => {
    if (!info?.join_url) return;
    try { await navigator.clipboard.writeText(info.join_url); toast.success("Link copied"); }
    catch { toast.error("Couldn't copy"); }
  };

  const regenerate = async () => {
    if (!window.confirm("Regenerate the join token? The previous link/QR will stop working.")) return;
    try {
      await api.post(`/events/${eventKind}/${eventId}/regenerate-token`);
      toast.success("New join link & QR generated");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const download = () => {
    if (!info?.qr_png_base64) return;
    const a = document.createElement("a");
    a.href = info.qr_png_base64;
    a.download = `pick2play-${eventKind}-${eventId.slice(0, 8)}-qr.png`;
    a.click();
  };

  const nativeShare = async () => {
    if (!info?.join_url) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: eventTitle || "Join my Pick2Play event",
          text: `Join "${eventTitle || "my event"}" on Pick2Play`,
          url: info.join_url,
        });
      } catch (err) {
        // AbortError = user dismissed the native share sheet — silently OK.
        if (err && err.name !== "AbortError") {
          // eslint-disable-next-line no-console
          console.warn("[share-dialog] navigator.share failed:", err);
        }
      }
    } else {
      copy();
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <button data-testid="share-trigger" className="pp-btn-secondary px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
            <QrCode size={14} /> Share / QR
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-[#131313] border-white/10 text-white max-w-md" data-testid="share-dialog">
        <DialogHeader><DialogTitle className="font-heading">Share this {eventKind}</DialogTitle></DialogHeader>

        {loading || !info ? (
          <div className="py-12 text-center text-zinc-500 text-sm">Loading…</div>
        ) : (
          <div className="space-y-4">
            <div className="flex justify-center bg-white p-4 rounded-xl" data-testid="share-qr">
              <img src={info.qr_png_base64} alt="Join QR" className="w-56 h-56" />
            </div>

            <div className="text-xs text-zinc-500 uppercase tracking-wider">Join link</div>
            <div className="pp-card p-3 flex items-center justify-between gap-2">
              <code className="text-[11px] text-[#00E676] truncate flex-1" data-testid="share-link">{info.join_url}</code>
              <button onClick={copy} data-testid="share-copy" title="Copy" className="p-2 rounded-lg border border-white/10 text-zinc-300 hover:text-[#00E676] hover:border-[#00E676]/40"><Copy size={14} /></button>
            </div>

            {info.visibility === "private" && (
              <div className="text-xs text-[#FFD60A] bg-[#FFD60A]/10 border border-[#FFD60A]/30 rounded-lg px-3 py-2">
                Private event — anyone with this link or QR can join, but it won't appear in public listings.
              </div>
            )}

            <div className="grid grid-cols-3 gap-2">
              <button onClick={nativeShare} data-testid="share-native" className="pp-btn-secondary px-3 py-2 rounded-lg text-xs flex items-center justify-center gap-1"><Share2 size={12} /> Share</button>
              <button onClick={download} data-testid="share-download" className="pp-btn-secondary px-3 py-2 rounded-lg text-xs flex items-center justify-center gap-1"><Download size={12} /> Save QR</button>
              <button onClick={regenerate} data-testid="share-regen" className="pp-btn-secondary px-3 py-2 rounded-lg text-xs flex items-center justify-center gap-1 text-[#FF3B30] border-[#FF3B30]/30"><RefreshCw size={12} /> Regen</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
